/**
 * Static Help Articles Data - Generated from master_help_documentation.md
 * 
 * This is a static, local frontend-only artifact.
 * No persistence, no backend storage, no DB writes, no API calls.
 */
export const helpArticles = [
  {
    slug: "create-account",
    title: "Create Your Account",
    category: "Getting Started",
    summary: "Sign up for Free Service Pro to start managing your service business.",
    prerequisites: ["A valid email address", "Basic company information"],
    steps: [
      "Go to freeservicepro.com",
      "Click \"Sign Up Free\"",
      "Enter your email address",
      "Create a secure password (minimum 8 characters)",
      "Click \"Create Account\"",
      "Check your email for a verification link",
      "Click the verification link to activate your account"
    ],
    expectedBehavior: ["You will receive a verification email within minutes", "After verification, you will be redirected to the setup wizard"],
    commonMistakes: ["Using a work email that blocks external emails - check spam folder", "Forgetting to verify email - you must verify before accessing your account"],
    relatedArticles: ["complete-initial-setup", "upload-company-logo"]
  },
  {
    slug: "complete-initial-setup",
    title: "Complete Initial Setup",
    category: "Getting Started",
    summary: "Set up your company profile to start using Free Service Pro.",
    prerequisites: ["Verified Free Service Pro account"],
    steps: [
      "After logging in, you will see the Setup Wizard",
      "Enter your company name",
      "Add your business phone number",
      "Enter your business address (street, city, state, zip)",
      "Set your default tax rate (if applicable)",
      "Configure your business hours (start and end time)",
      "Click \"Complete Setup\""
    ],
    expectedBehavior: ["Your company profile will be saved", "You will be redirected to the Dashboard"],
    commonMistakes: ["Skipping business hours - this affects calendar display", "Entering incorrect tax rate - this affects all estimates and invoices"],
    relatedArticles: ["upload-company-logo", "configure-business-hours"]
  },
  {
    slug: "upload-company-logo",
    title: "Upload Your Company Logo",
    category: "Getting Started",
    summary: "Add your company logo to personalize your estimates, invoices, and customer communications.",
    prerequisites: ["Completed initial setup", "Logo image file (PNG, JPG, or SVG recommended)"],
    steps: [
      "Go to Settings from the left sidebar",
      "Click the Branding tab",
      "Click \"Upload Logo\" or drag and drop your logo file",
      "Wait for the upload to complete",
      "Your logo will appear in the preview"
    ],
    expectedBehavior: ["Logo appears on all customer-facing documents", "Logo displays in email communications"],
    commonMistakes: ["Using a very large file - images are resized automatically but smaller files upload faster", "Using a low-resolution image - your logo may appear blurry"],
    relatedArticles: ["configure-email-templates"]
  },
  {
    slug: "create-customer",
    title: "Create a Customer",
    category: "Customers",
    summary: "Add a new customer to your Free Service Pro account.",
    prerequisites: ["Logged into Free Service Pro"],
    steps: [
      "Go to Customers from the left sidebar",
      "Click the + New Customer button in the top right",
      "Enter the customer's name (required)",
      "Enter email address (optional but recommended)",
      "Enter phone number (optional but recommended)",
      "Add a service address by clicking \"Add Address\"",
      "Click Create Customer"
    ],
    expectedBehavior: ["Customer appears in your customer list", "Customer can now be selected when creating jobs, estimates, or invoices"],
    commonMistakes: ["Not adding contact information - you won't be able to send estimates/invoices", "Forgetting to add service address - needed for scheduling visits"],
    relatedArticles: ["edit-customer", "import-customers-csv"]
  },
  {
    slug: "edit-customer",
    title: "Edit Customer Information",
    category: "Customers",
    summary: "Update an existing customer's details.",
    prerequisites: ["At least one customer in your account"],
    steps: [
      "Go to Customers from the left sidebar",
      "Click on the customer you want to edit",
      "Click the Edit button (pencil icon)",
      "Update the desired fields (name, email, phone)",
      "Click Save Changes"
    ],
    expectedBehavior: ["Customer information is updated immediately", "Changes reflect on all related jobs and invoices"],
    commonMistakes: ["Changing customer name after invoices sent - historical records keep original name"],
    relatedArticles: ["create-customer", "archive-customer"]
  },
  {
    slug: "import-customers-csv",
    title: "Import Customers from CSV",
    category: "Customers",
    summary: "Bulk import customers from a spreadsheet file.",
    prerequisites: ["CSV file with customer data"],
    steps: [
      "Go to Customers from the left sidebar",
      "Click the Import button",
      "Download the sample CSV template (optional)",
      "Prepare your CSV with columns: name, email, phone, address",
      "Click Upload CSV and select your file",
      "Review the preview of imported data",
      "Click Import Customers"
    ],
    expectedBehavior: ["Customers are created in bulk", "Summary shows successful and failed imports"],
    commonMistakes: ["Incorrect column headers - must match expected format", "Duplicate entries may create duplicates"],
    relatedArticles: ["create-customer"]
  },
  {
    slug: "archive-customer",
    title: "Archive a Customer",
    category: "Customers",
    summary: "Remove a customer from your active list without deleting their history.",
    prerequisites: ["Existing customer with no open jobs"],
    steps: [
      "Go to Customers and select a customer",
      "Click the three-dot menu",
      "Select Archive Customer",
      "Confirm the action"
    ],
    expectedBehavior: ["Customer is removed from active customer list", "Historical data (jobs, invoices) is preserved", "Customer can be restored later"],
    commonMistakes: ["Archiving customer with open jobs - complete or cancel jobs first"],
    relatedArticles: ["create-customer", "edit-customer"]
  },
  {
    slug: "create-job",
    title: "Create a Job",
    category: "Jobs",
    summary: "Create a new job to track work for a customer.",
    prerequisites: ["At least one customer in your account"],
    steps: [
      "Go to Jobs from the left sidebar",
      "Click + New Job",
      "Select a customer from the dropdown",
      "Enter a job title (e.g., \"AC Repair\", \"Plumbing Installation\")",
      "Add a description (optional)",
      "Select a service address",
      "Click Create Job"
    ],
    expectedBehavior: ["Job is created in \"Draft\" status", "Job appears in your jobs list", "You can now schedule visits for this job"],
    commonMistakes: ["Not selecting service address - needed for scheduling", "Creating duplicate jobs - check existing jobs first"],
    relatedArticles: ["schedule-visit", "add-job-notes", "complete-job"]
  },
  {
    slug: "add-job-notes",
    title: "Add Job Notes",
    category: "Jobs",
    summary: "Document important information about a job.",
    prerequisites: ["Existing job"],
    steps: [
      "Go to Jobs and select a job",
      "Scroll to the Notes section",
      "Click Add Note",
      "Enter your note text",
      "Click Save"
    ],
    expectedBehavior: ["Note is saved with timestamp", "Note appears in job timeline"],
    commonMistakes: ["Notes are visible to technicians - don't include sensitive billing info"],
    relatedArticles: ["upload-job-photos", "create-job"]
  },
  {
    slug: "upload-job-photos",
    title: "Upload Job Photos",
    category: "Jobs",
    summary: "Add photos to document job progress or issues.",
    prerequisites: ["Existing job", "Photo files on your device"],
    steps: [
      "Go to Jobs and select a job",
      "Scroll to the Photos section",
      "Click Upload Photos or drag and drop files",
      "Wait for upload to complete",
      "Add captions (optional)"
    ],
    expectedBehavior: ["Photos appear in job photo gallery", "Photos are viewable by technicians"],
    commonMistakes: ["Very large photo files may take longer to upload", "Photos cannot be edited after upload - only deleted"],
    relatedArticles: ["add-job-notes", "complete-job"]
  },
  {
    slug: "complete-job",
    title: "Complete a Job",
    category: "Jobs",
    summary: "Mark a job as finished when all work is done.",
    prerequisites: ["Job with all visits completed"],
    steps: [
      "Go to Jobs and select a job",
      "Review that all visits are completed",
      "Click the Complete Job button",
      "Confirm the action"
    ],
    expectedBehavior: ["Job status changes to \"Completed\"", "Job is removed from active jobs view", "Job history is preserved"],
    commonMistakes: ["Completing job before all visits done - complete visits first", "Completing job before invoicing - create invoice first if needed"],
    relatedArticles: ["create-invoice", "schedule-visit"]
  },
  {
    slug: "create-estimate",
    title: "Create an Estimate",
    category: "Estimates",
    summary: "Create a price quote for potential work.",
    prerequisites: ["At least one customer in your account"],
    steps: [
      "Go to Estimates from the left sidebar",
      "Click + New Estimate",
      "Select a customer",
      "Add line items with description, quantity, and unit price",
      "Set tax rate (if applicable)",
      "Add deposit requirement (optional)",
      "Click Save Estimate"
    ],
    expectedBehavior: ["Estimate is created in \"Draft\" status", "Totals are calculated automatically", "Estimate can be sent to customer"],
    commonMistakes: ["Forgetting tax rate - check your default in Settings", "No line items - estimate must have at least one item"],
    relatedArticles: ["send-estimate", "add-deposit-estimate", "convert-estimate-to-job"]
  },
  {
    slug: "send-estimate",
    title: "Send an Estimate",
    category: "Estimates",
    summary: "Email an estimate to your customer for review.",
    prerequisites: ["Completed estimate with line items", "Customer has email address"],
    steps: [
      "Go to Estimates and select an estimate",
      "Review the estimate details",
      "Click Send Estimate",
      "Confirm the recipient email",
      "Click Send"
    ],
    expectedBehavior: ["Customer receives email with estimate link", "Estimate status changes to \"Sent\"", "Customer can view, approve, or reject online"],
    commonMistakes: ["Customer email missing - add email before sending", "Sending incomplete estimate - review all line items first"],
    relatedArticles: ["create-estimate"]
  },
  {
    slug: "add-deposit-estimate",
    title: "Add Deposit to Estimate",
    category: "Estimates",
    summary: "Request a deposit payment before starting work.",
    prerequisites: ["Existing estimate"],
    steps: [
      "Go to Estimates and select an estimate",
      "Scroll to Deposit Settings",
      "Toggle Require Deposit on",
      "Enter deposit amount or percentage",
      "Click Save"
    ],
    expectedBehavior: ["Deposit amount shows on estimate", "Customer can pay deposit when viewing estimate", "Deposit status is tracked"],
    commonMistakes: ["Deposit exceeds total - deposit should be less than estimate total"],
    relatedArticles: ["create-estimate", "send-estimate"]
  },
  {
    slug: "convert-estimate-to-job",
    title: "Convert Estimate to Job",
    category: "Estimates",
    summary: "Create a job from an approved estimate.",
    prerequisites: ["Approved estimate"],
    steps: [
      "Go to Estimates and select an estimate",
      "Click the three-dot menu",
      "Select Convert to Job",
      "Confirm the action"
    ],
    expectedBehavior: ["New job is created with estimate details", "Job is linked to original estimate", "You can now schedule visits"],
    commonMistakes: ["Converting unapproved estimate - may need customer approval first"],
    relatedArticles: ["create-estimate", "schedule-visit"]
  },
  {
    slug: "create-invoice",
    title: "Create an Invoice",
    category: "Invoices",
    summary: "Bill a customer for work completed.",
    prerequisites: ["At least one customer in your account"],
    steps: [
      "Go to Invoices from the left sidebar",
      "Click + New Invoice",
      "Select a customer",
      "Add line items with description, quantity, and unit price",
      "Set tax rate (if applicable)",
      "Click Save Invoice"
    ],
    expectedBehavior: ["Invoice is created in \"Draft\" status", "Invoice number is assigned automatically", "Totals calculate automatically"],
    commonMistakes: ["Wrong customer selected - verify before adding items", "Missing line items - invoice must have at least one item"],
    relatedArticles: ["send-invoice", "record-payment"]
  },
  {
    slug: "send-invoice",
    title: "Send an Invoice",
    category: "Invoices",
    summary: "Email an invoice to your customer.",
    prerequisites: ["Completed invoice with line items", "Customer has email address"],
    steps: [
      "Go to Invoices and select an invoice",
      "Review invoice details",
      "Click Send Invoice",
      "Confirm recipient email",
      "Click Send"
    ],
    expectedBehavior: ["Customer receives email with invoice link", "Invoice status changes to \"Sent\"", "Customer can pay online (if payments enabled)"],
    commonMistakes: ["Customer email missing - add email first", "Sending unpaid invoice again - check payment status first"],
    relatedArticles: ["create-invoice", "record-payment"]
  },
  {
    slug: "record-payment",
    title: "Record a Payment",
    category: "Invoices",
    summary: "Manually record a payment received for an invoice.",
    prerequisites: ["Sent invoice", "Payment received (cash, check, etc.)"],
    steps: [
      "Go to Invoices and select an invoice",
      "Click Record Payment",
      "Enter payment amount",
      "Select payment method (cash, check, etc.)",
      "Enter payment date",
      "Add notes (optional)",
      "Click Save Payment"
    ],
    expectedBehavior: ["Payment is recorded against invoice", "Invoice balance updates", "If fully paid, invoice status changes to \"Paid\""],
    commonMistakes: ["Recording same payment twice - check payment history first", "Wrong amount - verify against actual payment received"],
    relatedArticles: ["send-invoice", "create-invoice"]
  },
  {
    slug: "schedule-visit",
    title: "Schedule a Visit",
    category: "Scheduling",
    summary: "Add a visit to your calendar for a job.",
    prerequisites: ["Existing job"],
    steps: [
      "Go to Calendar from the left sidebar",
      "Click on a time slot, or click + New Visit",
      "Select a job from the dropdown",
      "Set the date and time",
      "Set the duration (end time)",
      "Assign technicians (optional)",
      "Click Schedule Visit"
    ],
    expectedBehavior: ["Visit appears on calendar", "Visit is linked to the job", "Assigned technicians can see the visit"],
    commonMistakes: ["Double-booking - check calendar for conflicts", "Wrong date selected - verify the date before saving"],
    relatedArticles: ["reschedule-visit", "complete-visit"]
  },
  {
    slug: "reschedule-visit",
    title: "Reschedule a Visit",
    category: "Scheduling",
    summary: "Move a scheduled visit to a different date or time.",
    prerequisites: ["Existing scheduled visit"],
    steps: [
      "Go to Calendar",
      "Click and hold on the visit",
      "Drag to the new date/time",
      "Release to drop",
      "Or: Click on visit, click Edit, change date/time, click Save"
    ],
    expectedBehavior: ["Visit moves to new time slot", "Job timeline updates", "Technician assignments are preserved"],
    commonMistakes: ["Dragging to occupied slot - check for conflicts", "Forgetting to notify customer - send reminder if needed"],
    relatedArticles: ["schedule-visit", "complete-visit"]
  },
  {
    slug: "complete-visit",
    title: "Complete a Visit",
    category: "Scheduling",
    summary: "Mark a visit as finished after the work is done.",
    prerequisites: ["Scheduled or in-progress visit"],
    steps: [
      "Go to Calendar and click on the visit",
      "Click Complete Visit",
      "Add completion notes (optional)",
      "Click Confirm"
    ],
    expectedBehavior: ["Visit status changes to \"Completed\"", "Visit shows as completed on calendar", "Job timeline updates"],
    commonMistakes: ["Completing before work done - wait until actually finished", "Not adding notes - completion notes help track what was done"],
    relatedArticles: ["schedule-visit", "complete-job"]
  },
  {
    slug: "add-technician",
    title: "Add a Technician",
    category: "Technicians",
    summary: "Invite a team member to access the technician portal.",
    prerequisites: ["Technician's email address"],
    steps: [
      "Go to Settings from the left sidebar",
      "Click the Technicians tab",
      "Click + Add Technician",
      "Enter technician's name",
      "Enter technician's email",
      "Enter technician's phone (optional)",
      "Select a color for calendar display",
      "Click Send Invite"
    ],
    expectedBehavior: ["Technician receives email invitation", "Technician appears in your list (pending until accepted)", "After accepting, technician can access their portal"],
    commonMistakes: ["Wrong email address - verify before sending invite", "Technician doesn't receive email - check spam folder"],
    relatedArticles: ["assign-technicians-visit"]
  },
  {
    slug: "assign-technicians-visit",
    title: "Assign Technicians to Visit",
    category: "Technicians",
    summary: "Assign team members to a scheduled visit.",
    prerequisites: ["At least one active technician", "Scheduled visit"],
    steps: [
      "Go to Calendar and click on a visit",
      "Click Edit or find the Assign Technicians section",
      "Click the technician dropdown",
      "Select one or more technicians",
      "Click Save"
    ],
    expectedBehavior: ["Technicians appear on the visit", "Visit shows in technician's portal", "Calendar shows technician colors"],
    commonMistakes: ["Assigning unavailable technician - check their schedule first"],
    relatedArticles: ["add-technician", "schedule-visit"]
  },
  {
    slug: "configure-reminders",
    title: "Configure Appointment Reminders",
    category: "Communications",
    summary: "Set up automatic reminders for customer appointments.",
    prerequisites: ["Appointment Reminders feature enabled"],
    steps: [
      "Go to Settings from the left sidebar",
      "Click Reminders in the left menu",
      "Toggle Enable Appointment Reminders on",
      "Set reminder timing (e.g., 24 hours before)",
      "Choose reminder method (Email, SMS, or Both)",
      "Click Save Settings"
    ],
    expectedBehavior: ["Customers receive reminders before appointments", "Reminders include appointment date, time, and your company info", "Times display in your company's timezone"],
    commonMistakes: ["Reminders disabled - check toggle is on", "Customer missing contact info - must have email/phone"],
    relatedArticles: ["edit-email-templates"]
  },
  {
    slug: "edit-email-templates",
    title: "Edit Email Templates",
    category: "Communications",
    summary: "Customize the emails sent to your customers.",
    prerequisites: ["Access to Settings"],
    steps: [
      "Go to Settings from the left sidebar",
      "Click the Email Templates tab",
      "Select a template to edit",
      "Edit the subject line",
      "Edit the email body content",
      "Click Preview to see how it looks",
      "Click Save Template"
    ],
    expectedBehavior: ["Template updates for future emails", "Preview shows actual formatting", "You can send a test email to yourself"],
    commonMistakes: ["Deleting required variables - some fields like {customer_name} are needed", "Not saving changes - click Save before leaving"],
    relatedArticles: ["configure-reminders"]
  },
  {
    slug: "configure-business-hours",
    title: "Configure Business Hours",
    category: "Settings",
    summary: "Set your company's working hours for the calendar.",
    prerequisites: ["Access to Settings"],
    steps: [
      "Go to Settings from the left sidebar",
      "Click the Company tab",
      "Find Business Hours section",
      "Set start time (e.g., 7:00 AM)",
      "Set end time (e.g., 7:00 PM)",
      "Click Save Changes"
    ],
    expectedBehavior: ["Calendar displays your business hours", "Non-business hours are grayed out", "Scheduling outside hours shows warning"],
    commonMistakes: ["Setting wrong timezone - business hours use your company timezone"],
    relatedArticles: ["schedule-visit", "complete-initial-setup"]
  },
  {
    slug: "manage-service-catalog",
    title: "Manage Service Catalog",
    category: "Settings",
    summary: "Create reusable service items for estimates and invoices.",
    prerequisites: ["Access to Settings"],
    steps: [
      "Go to Settings from the left sidebar",
      "Click the Catalog tab",
      "Click + Add Item",
      "Enter item name",
      "Enter description",
      "Enter default price",
      "Click Save"
    ],
    expectedBehavior: ["Item appears in your catalog", "Item can be selected when creating estimates/invoices", "Prices can be adjusted per document"],
    commonMistakes: ["Duplicate items - search catalog before adding"],
    relatedArticles: ["create-estimate", "create-invoice"]
  },
  {
    slug: "view-subscription",
    title: "View Your Subscription",
    category: "Billing",
    summary: "Check your current plan and features.",
    prerequisites: ["Logged into Free Service Pro"],
    steps: [
      "Go to Settings from the left sidebar",
      "Click Billing in the left menu",
      "View your current plan details",
      "See included features",
      "View billing history"
    ],
    expectedBehavior: ["Current plan name displays", "Feature list shows what's included", "Upgrade options are visible"],
    commonMistakes: ["Confusing free features with paid - check feature requirements"],
    relatedArticles: ["upgrade-plan"]
  },
  {
    slug: "upgrade-plan",
    title: "Upgrade Your Plan",
    category: "Billing",
    summary: "Add features by upgrading your subscription.",
    prerequisites: ["Active Free Service Pro account"],
    steps: [
      "Go to Settings from the left sidebar",
      "Click Upgrades in the left menu",
      "Browse available feature bundles",
      "Click Upgrade on desired bundle",
      "Enter payment information",
      "Confirm purchase"
    ],
    expectedBehavior: ["New features unlock immediately", "Billing starts according to bundle terms", "Feature appears in your entitlements"],
    commonMistakes: ["Duplicate purchases - check current features first"],
    relatedArticles: ["view-subscription"]
  },
  {
    slug: "customer-not-receiving-emails",
    title: "Customer Not Receiving Emails",
    category: "Troubleshooting",
    summary: "Diagnose and fix email delivery issues.",
    prerequisites: ["Customer with email address on file"],
    steps: [
      "Verify customer email is correct",
      "Ask customer to check spam/junk folder",
      "Go to customer detail page",
      "Check for Delivery Issues warning",
      "If bounced, update email address",
      "Resend the email"
    ],
    expectedBehavior: ["Delivery issues display warnings", "Updated emails are validated", "Resent emails should deliver"],
    commonMistakes: ["Typo in email address - double-check spelling", "Corporate email blocking - try personal email"],
    relatedArticles: ["edit-customer", "edit-email-templates"]
  },
  {
    slug: "visit-times-wrong",
    title: "Visit Times Showing Wrong",
    category: "Troubleshooting",
    summary: "Fix timezone display issues on calendar.",
    prerequisites: ["Access to Settings"],
    steps: [
      "Go to Settings > Company",
      "Verify your business timezone is correct",
      "Save any changes",
      "Refresh the calendar page",
      "Visits should now show correct times"
    ],
    expectedBehavior: ["All times display in your business timezone", "Customer notifications use your timezone", "Calendar matches what you entered"],
    commonMistakes: ["Browser in different timezone - times are converted for display", "DST transitions - clocks adjust automatically"],
    relatedArticles: ["configure-business-hours", "schedule-visit"]
  }
];

// Category list for filtering
export const helpCategories = [
  "Getting Started",
  "Customers",
  "Jobs",
  "Estimates",
  "Invoices",
  "Scheduling",
  "Technicians",
  "Communications",
  "Settings",
  "Billing",
  "Troubleshooting"
];

// Simple client-side search function
export function searchHelpArticles(query, articles = helpArticles) {
  if (!query || query.trim().length < 2) {
    return articles.slice(0, 6); // Return first 6 as default
  }
  
  const lowerQuery = query.toLowerCase().trim();
  const words = lowerQuery.split(/\s+/);
  
  return articles
    .map(article => {
      let score = 0;
      const searchableText = [
        article.title,
        article.category,
        article.summary,
        ...(article.steps || []),
        ...(article.expectedBehavior || []),
        ...(article.commonMistakes || []),
        ...(article.prerequisites || [])
      ].join(' ').toLowerCase();
      
      // Score based on matches
      for (const word of words) {
        if (article.title.toLowerCase().includes(word)) score += 10;
        if (article.category.toLowerCase().includes(word)) score += 5;
        if (article.summary.toLowerCase().includes(word)) score += 3;
        if (searchableText.includes(word)) score += 1;
      }
      
      return { ...article, score };
    })
    .filter(article => article.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 8);
}
